import type { LucideIcon } from "lucide-react";
import { matchPath } from "react-router";
import type { MeResponse } from "@klantpunt/shared";
import { hasModule } from "../lib/auth";
import {
  IconAddressBook,
  IconAssets,
  IconBoard,
  IconBuilding,
  IconContract,
  IconCondition,
  IconEquipment,
  IconFile,
  IconInspections,
  IconInbox,
  IconIssues,
  IconMap,
  IconMaterials,
  IconMeter,
  IconMjop,
  IconOverview,
  IconPlanning,
  IconSettings,
  IconTasks,
} from "./icons";

type Me = MeResponse | null | undefined;

/** Onderdelen van een gebouw, elk met een eigen adres: `/gebouwen/:id/<onderdeel>`. */
export const BUILDING_SECTIONS = [
  "overzicht",
  "tekening",
  "bevindingen",
  "taken",
  "assets",
  "arbeidsmiddelen",
  "meters",
  "keuringen",
  "materialen",
  "contracten",
  "dossier",
  "gebouw",
] as const;
export type BuildingSection = (typeof BUILDING_SECTIONS)[number];
export const isBuildingSection = (s: string | undefined): s is BuildingSection =>
  (BUILDING_SECTIONS as readonly string[]).includes(s ?? "");

export const buildingPath = (id: string, section: BuildingSection) => `/gebouwen/${id}/${section}`;

/** Onderdeel van het huidige pad: `/gebouwen/:id` zonder onderdeel is de tekening. */
export function sectionOfPath(pathname: string): BuildingSection | null {
  const m = matchPath("/gebouwen/:id/:section", pathname);
  if (m) return isBuildingSection(m.params.section) ? m.params.section : null;
  return matchPath("/gebouwen/:id", pathname) ? "tekening" : null;
}

export interface NavItem {
  key: string;
  to: string;
  label: string;
  title: string;
  Icon: LucideIcon;
  /** Is dit item de huidige pagina? */
  active: (pathname: string) => boolean;
  /** Teller achter het label (Inbox: te beoordelen). */
  count?: number;
}

export interface NavGroup {
  /** Kopje boven de groep; zonder kopje staan de items los. */
  label?: string;
  items: NavItem[];
}

const startsWith = (to: string) => (p: string) => p === to || p.startsWith(`${to}/`);

const item = (
  key: string,
  to: string,
  label: string,
  Icon: LucideIcon,
  active: (p: string) => boolean = startsWith(to),
  title = label,
): NavItem => ({ key, to, label, title, Icon, active });

/**
 * Het menu (rail op desktop, sheet "Menu" op de telefoon). Met een gekozen gebouw: de onderdelen van dat
 * gebouw, in groepen. Bij "Alle gebouwen": de gebouwenlijst en de overzichten over alle gebouwen.
 * Mijn taken staat altijd bovenaan; beheer zit in het profielmenu.
 */
export function navGroups(me: Me, buildingId: string | null): NavGroup[] {
  const issues = hasModule(me, "issues");
  const assets = hasModule(me, "assets");
  const meters = hasModule(me, "meters");
  const mjop = hasModule(me, "mjop");
  const condition = hasModule(me, "condition");
  const directory = hasModule(me, "directory");
  const intake = hasModule(me, "intake");
  const dossier = hasModule(me, "dossier");
  const materials = hasModule(me, "materials");
  const contracts = hasModule(me, "contracts");
  const on = (ok: boolean, it: NavItem) => (ok ? [it] : []);
  const mine = [
    ...on(issues, item("mijntaken", "/taken", "Mijn taken", IconTasks)),
    // Inbox Te beoordelen (meldingen van buiten): over alle gebouwen, met teller (Layout).
    ...on(intake, item("inbox", "/inbox", "Inbox", IconInbox, startsWith("/inbox"), "Inbox: meldingen te beoordelen")),
  ];
  // Adresboek hoort niet bij één gebouw: altijd bovenin, onder Mijn taken (met of zonder gekozen gebouw).
  const book = on(
    directory,
    item(
      "adresboek",
      "/adresboek",
      "Adresboek",
      IconAddressBook,
      startsWith("/adresboek"),
      "Leveranciers, huurders en contacten",
    ),
  );

  let groups: NavGroup[];
  if (buildingId) {
    const sec = (s: BuildingSection) => buildingPath(buildingId, s);
    const isSec = (s: BuildingSection) => (p: string) => sectionOfPath(p) === s;
    groups = [
      { items: [...mine, ...book] },
      {
        items: [
          item("overzicht", sec("overzicht"), "Overzicht", IconOverview, isSec("overzicht")),
          item("tekening", sec("tekening"), "Tekening", IconMap, isSec("tekening")),
          ...on(
            dossier,
            item("dossier", sec("dossier"), "Dossier", IconFile, isSec("dossier"), "Documenten en tekeningen"),
          ),
        ],
      },
      {
        label: "Assets",
        items: [
          ...on(assets, item("assets", sec("assets"), "Assets", IconAssets, isSec("assets"))),
          ...on(
            assets,
            item("arbeidsmiddelen", sec("arbeidsmiddelen"), "Arbeidsmiddelen", IconEquipment, isSec("arbeidsmiddelen")),
          ),
          ...on(meters && assets, item("meters", sec("meters"), "Meters", IconMeter, isSec("meters"))),
        ],
      },
      {
        label: "Uitvoering",
        items: [
          ...on(
            assets || issues || meters,
            item(
              "planning",
              "/planning",
              "Planning",
              IconPlanning,
              startsWith("/planning"),
              "Onderhoud, keuringen en taken",
            ),
          ),
          ...on(issues, item("taken", sec("taken"), "Taken", IconBoard, isSec("taken"))),
          ...on(
            assets,
            item("keuringen", sec("keuringen"), "Onderhoud en keuringen", IconInspections, isSec("keuringen")),
          ),
          ...on(
            materials,
            item(
              "materialen",
              sec("materialen"),
              "Materialen",
              IconMaterials,
              isSec("materialen"),
              "Artikelen en reservevoorraad",
            ),
          ),
          ...on(
            contracts,
            item(
              "contracten",
              sec("contracten"),
              "Contracten",
              IconContract,
              isSec("contracten"),
              "Onderhoudscontracten met leveranciers",
            ),
          ),
          ...on(issues, item("bevindingen", sec("bevindingen"), "Bevindingen", IconIssues, isSec("bevindingen"))),
        ],
      },
      {
        label: "Rapporten",
        items: [
          ...on(mjop, item("mjop", "/mjop", "MJOP", IconMjop, startsWith("/mjop"), "Meerjarenonderhoudsplan")),
          ...on(
            condition,
            item(
              "conditie",
              "/conditie",
              "Conditie",
              IconCondition,
              startsWith("/conditie"),
              "Conditiemeting NEN 2767",
            ),
          ),
        ],
      },
      {
        items: [
          item(
            "gebouw",
            sec("gebouw"),
            "Gebouw",
            IconSettings,
            isSec("gebouw"),
            "Gegevens, verdiepingen en notificaties",
          ),
        ],
      },
    ];
  } else {
    groups = [
      { items: [...mine, ...book] },
      {
        items: [
          item("gebouwen", "/", "Gebouwen", IconBuilding, (p) => p === "/" || p.startsWith("/gebouwen/")),
          ...on(
            assets || issues || meters,
            item(
              "planning",
              "/planning",
              "Planning",
              IconPlanning,
              startsWith("/planning"),
              "Onderhoud, keuringen en taken",
            ),
          ),
          ...on(meters, item("meters", "/meterstanden", "Meterstanden", IconMeter)),
          ...on(assets, item("keuringen", "/keuringen", "Onderhoud en keuringen", IconInspections)),
          ...on(dossier, item("dossiers", "/dossiers", "Dossiers", IconFile)),
          ...on(materials, item("bestellen", "/bestellen", "Te bestellen", IconMaterials)),
          ...on(
            contracts,
            item(
              "contracten",
              "/contracten",
              "Contracten",
              IconContract,
              startsWith("/contracten"),
              "Onderhoudscontracten met leveranciers",
            ),
          ),
          ...on(mjop, item("mjop", "/mjop", "MJOP", IconMjop, startsWith("/mjop"), "Meerjarenonderhoudsplan")),
          ...on(
            condition,
            item(
              "conditie",
              "/conditie",
              "Conditie",
              IconCondition,
              startsWith("/conditie"),
              "Conditiemeting NEN 2767",
            ),
          ),
        ],
      },
    ];
  }
  return groups.filter((g) => g.items.length > 0);
}

/** Onderbalk op de telefoon, links van de (+): in een gebouw Overzicht en Tekening, anders Gebouwen en Mijn taken. */
export function tabItems(groups: NavGroup[], buildingId: string | null): NavItem[] {
  const all = groups.flatMap((g) => g.items);
  const keys = buildingId ? ["overzicht", "tekening"] : ["gebouwen", "mijntaken"];
  return keys.flatMap((k) => all.filter((i) => i.key === k));
}

/** Profiel staat in het profielmenu en onderaan de sheet "Menu". */
export const PROFILE_PATH = "/profiel";

/** Hulp staat onderaan de rail en in de sheet "Menu"; het pad krijgt "?vanaf=" mee in Layout. */
export const HELP_PATH = "/hulp";

/** Organisatie-instellingen (gebruikers, modules, rapportstijl): in het profielmenu, voor beheerders. */
export const ORG_PATH = "/gebruikers";
